import cloneDeep from 'lodash.clonedeep';
import { useMemo, useState, useEffect } from 'react';
import { Tab, Tabs } from 'react-bootstrap';
import { Button } from '@progress/kendo-react-buttons';
import { WindowActionsBar, Window } from '@progress/kendo-react-dialogs';
import { useLocation } from 'react-router-dom';

import { Algorithm } from '../../types/algorithm';
import { GameDocument } from '../../types/game-document';
import EventAlgorithm from '../../components/map/event-configuration/event--algorithm';

interface WorldMapEventProps {
  title?: string;
  zoneId?: string;
  gameDocument?: GameDocument;
  onCancel?: () => void;
  onConfirm: (
    algorithmOnEnter: Algorithm,
    algorithmOnExit: Algorithm,
    algorithmOnTaskCompleted: Algorithm
  ) => void;
}

const WorldMapEvent = ({
  zoneId = '',
  gameDocument,
  title,
  onCancel,
  onConfirm
}: WorldMapEventProps) => {
  const { state: stateLocation } = useLocation();
  const [activeTab, setActiveTab] = useState('onEnter');

  const [algorithmOnExit, setAlgorithmOnExit] = useState<Algorithm>({});
  const [algorithmOnEnter, setAlgorithmOnEnter] = useState<Algorithm>({});
  const [algorithmOnTaskCompleted, setAlgorithmOnTaskCompleted] =
    useState<Algorithm>({});

  const gameEvents = useMemo(() => {
    if (!gameDocument) return {};

    let onEnter: Algorithm = {};
    let onExit: Algorithm = {};
    let onTaskCompleted: Algorithm = {};
    if (zoneId === '') {
      //World-map
      const worldMap = cloneDeep(gameDocument.rules.worldMap);
      if (worldMap) {
        onEnter = worldMap?.events?.onEnterMap!;
        onExit = worldMap?.events?.onExitMap!;
        onTaskCompleted = worldMap?.events?.onTaskCompletedMap!;
      }
    } else {
      //Zone
      const zone = cloneDeep(
        gameDocument?.assets.zones?.find((x) => x.id === zoneId)
      );
      if (zone) {
        onEnter = zone?.events?.onEnterMap!;
        onExit = zone?.events?.onExitMap!;
        onTaskCompleted = zone?.events?.onTaskCompletedMap!;
      }
    }

    setAlgorithmOnEnter(onEnter);
    setAlgorithmOnExit(onExit);
    setAlgorithmOnTaskCompleted(onTaskCompleted);

    return { onEnter, onExit, onTaskCompleted };
  }, [gameDocument, zoneId]);

  useEffect(() => {
    if (stateLocation?.eventType) {
      const mapping: Record<string, string> = {
        onEnterMap: 'onEnter',
        onExitMap: 'onExit',
        onTaskCompletedMap: 'onTaskCompleted'
      };
      const mappedTab = mapping[stateLocation.eventType];
      if (mappedTab) {
        setActiveTab(mappedTab);
      }
    }
  }, [stateLocation?.eventType]);

  const handleEventChange = (
    event: 'onEnter' | 'onExit' | 'onTaskCompleted',
    algorithm: Algorithm
  ) => {
    if (event === 'onEnter') setAlgorithmOnEnter(algorithm);
    if (event === 'onExit') setAlgorithmOnExit(algorithm);
    if (event === 'onTaskCompleted') setAlgorithmOnTaskCompleted(algorithm);
  };

  return (
    <Window
      onClose={onCancel}
      title={title}
      modal={true}
      className={'shadow'}
      initialWidth={800}
      initialHeight={650}
      minimizeButton={undefined}
      maximizeButton={undefined}>
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k ?? 'onEnter')}>
        <Tab eventKey="onEnter" title="Entered">
          <EventAlgorithm
            action={`${zoneId ? 'ZoneMap' : 'WorldMap'}.onEnter`}
            description={`This runs when a player enters this ${zoneId ? 'zone map' : 'map'}.`}
            shortcutText={`What happens when a player enters this ${zoneId ? 'zone map' : 'map'}?`}
            onChange={(e) => handleEventChange('onEnter', e)}
            algorithm={gameEvents.onEnter}
          />
        </Tab>

        <Tab eventKey="onExit" title="Exited">
          <EventAlgorithm
            action={`${zoneId ? 'ZoneMap' : 'WorldMap'}.onExit`}
            description={`This runs when a player exits this ${zoneId ? 'zone map' : 'map'}.`}
            onChange={(e) => handleEventChange('onExit', e)}
            algorithm={gameEvents.onExit}
            shortcutText={`What happens when a player exits this ${zoneId ? 'zone map' : 'map'}?`}
          />
        </Tab>

        <Tab eventKey={'onTaskCompleted'} title={'Task Completed'}>
          <EventAlgorithm
            onChange={(e) => handleEventChange('onTaskCompleted', e)}
            algorithm={gameEvents.onTaskCompleted}
            action={`${zoneId ? 'ZoneMap' : 'WorldMap'}.onTaskCompleted`}
            description={`This runs when a player completes a task in this ${zoneId ? 'zone map' : 'map'}.`}
            shortcutText={`What happens when a player completes a task in this ${zoneId ? 'zone map' : 'map'}?`}
          />
        </Tab>
      </Tabs>

      <WindowActionsBar>
        <Button themeColor={'secondary'} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          themeColor={'primary'}
          onClick={() =>
            onConfirm(
              algorithmOnEnter,
              algorithmOnExit,
              algorithmOnTaskCompleted
            )
          }>
          Confirm
        </Button>
      </WindowActionsBar>
    </Window>
  );
};

export default WorldMapEvent;
